// Arte das rodas (pixel art desenhada em código, no estilo dos móveis): a tela da TV com o programa
// (futebol, novela, desenho) e com o jogo, a tela do fliperama, a bolinha do pingue-pongue, a moedinha
// que voa no jokenpô, o balde de pipoca e o brilho do espelho.
//
// Só retângulos inteiros de pixel (nada de curvas suaves): a escala vem da câmera. Cada função
// desenha em coordenadas de mundo (px de arte) e não guarda estado: o quadro vem de `t` (ms).

import type { Programa } from '../../core/rodas.js';

type Ctx = CanvasRenderingContext2D;

const TV_W = 18;
const TV_H = 11;
/** Tela da TV e do fliperama no sprite (px a partir do canto do móvel): ver art/furniture/common.ts. */
export const TV_TELA = { dx: 7, dy: 2, w: TV_W, h: TV_H } as const;
export const FLIPERAMA_TELA = { dx: 3, dy: 8, w: 10, h: 9 } as const;

function rect(c: Ctx, x: number, y: number, w: number, h: number, cor: string): void {
  c.fillStyle = cor;
  c.fillRect(Math.round(x), Math.round(y), w, h);
}

/** Quadro de 0 a n-1 no instante t (sempre 0 com movimento reduzido). */
function quadro(t: number, ms: number, n: number, reduzido: boolean): number {
  return reduzido ? 0 : Math.floor(t / ms) % n;
}

/** Tela da TV (18×11) com o programa no ar. `lance` é o gol, o susto ou a piada do momento. */
export function desenhaTv(c: Ctx, x: number, y: number, programa: Programa | 'jogo', t: number, lance: boolean, reduzido: boolean): void {
  if (programa === 'futebol') {
    rect(c, x, y, TV_W, TV_H, '#2f8f3f');
    rect(c, x, y + 5, TV_W, 1, '#9fe0a8'); // linha do meio
    rect(c, x + 8, y + 4, 2, 3, '#9fe0a8'); // círculo
    rect(c, x, y + 3, 3, 5, '#7ac98a'); // área
    rect(c, x + 15, y + 3, 3, 5, '#7ac98a');
    const q = quadro(t, 250, 6, reduzido);
    const bx = [3, 6, 9, 12, 9, 6][q]!;
    const by = [4, 6, 3, 7, 5, 8][q]!;
    rect(c, x + bx - 3, y + by, 2, 2, '#1a3a8a'); // jogador azul
    rect(c, x + bx + 4, y + (by + 3) % 8 + 1, 2, 2, '#d63b3b'); // jogador vermelho
    rect(c, x + bx, y + by + 1, 1, 1, '#ffffff'); // bola
    if (lance) rect(c, x + 14, y + 4, 3, 3, '#f2e45b'); // gol: a rede acende
  } else if (programa === 'novela') {
    rect(c, x, y, TV_W, TV_H, '#7a3d5c');
    rect(c, x, y + 8, TV_W, 3, '#4a2540'); // sofá do cenário
    const q = quadro(t, 600, 2, reduzido);
    // dois rostos de perfil: um fala, o outro escuta
    rect(c, x + 3, y + 3, 4, 5, '#f0c8a0');
    rect(c, x + 3, y + 2, 4, 2, '#5a3220');
    rect(c, x + 11, y + 3, 4, 5, '#e8b890');
    rect(c, x + 11, y + 2, 4, 2, '#e8d060');
    rect(c, x + 4, y + 6 + q, 2, 1, '#c03040'); // boca falando
    if (lance) {
      rect(c, x + 8, y + 1, 2, 3, '#ffffff'); // lágrima de drama
      rect(c, x + 8, y + 4, 2, 1, '#6cc8ff');
    }
  } else if (programa === 'desenho') {
    rect(c, x, y, TV_W, TV_H, '#6cc8ff');
    rect(c, x, y + 8, TV_W, 3, '#7ad860');
    const q = quadro(t, 200, 8, reduzido);
    const hop = [0, 2, 3, 2, 0, 2, 3, 2][q]!;
    rect(c, x + 3, y + 5 - hop, 4, 3, '#ff7ab0'); // bichinho
    rect(c, x + 4, y + 4 - hop, 1, 1, '#ffffff');
    rect(c, x + 12 - (q % 4), y + 3 + hop / 2, 3, 3, '#ffd23f'); // sol saltitante
    if (lance) rect(c, x + 8, y + 1, 2, 2, '#ffffff');
  } else {
    // jogo: raquetes e bolinha, no estilo dos primeiros videogames
    rect(c, x, y, TV_W, TV_H, '#10142a');
    for (let i = 1; i < TV_H; i += 2) rect(c, x + 8, y + i, 1, 1, '#3a4aa0');
    const q = quadro(t, 120, 24, reduzido);
    const ball = q < 12 ? q : 24 - q;
    rect(c, x + 2 + ball, y + 2 + ((q * 3) % 7), 2, 2, '#ffffff');
    rect(c, x + 1, y + 2 + ((q + 4) % 6), 1, 4, '#6cc8ff');
    rect(c, x + 16, y + 3 + ((q + 9) % 5), 1, 4, '#ff7ab0');
  }
}

/** Tela do fliperama (10×9): pontinhos correndo e a nave. */
export function desenhaFliperamaTela(c: Ctx, x: number, y: number, t: number, reduzido: boolean): void {
  rect(c, x, y, 10, 9, '#0a1030');
  const q = quadro(t, 180, 6, reduzido);
  for (let i = 0; i < 3; i++) rect(c, x + 1 + ((i * 4 + q * 2) % 8), y + ((q + i * 3) % 6), 1, 1, '#ffe66b');
  rect(c, x + 2 + ((q * 3) % 5), y + 7, 3, 1, '#6cffb0'); // nave
  rect(c, x + 3 + ((q * 3) % 5), y + 6, 1, 1, '#6cffb0');
  if (q % 3 === 0) rect(c, x + 4 + ((q * 3) % 5), y + 3, 1, 3, '#ff6b6b'); // tiro
}

/** Bolinha do pingue-pongue (2×2) com a sombra na mesa. */
export function desenhaBola(c: Ctx, x: number, y: number, altura: number): void {
  rect(c, x, y + 1, 2, 1, 'rgba(0,0,0,0.25)');
  rect(c, x, y - altura, 2, 2, '#ffffff');
  rect(c, x + 1, y - altura + 1, 1, 1, '#f0c050');
}

/** Moedinha dourada (4×4) girando: `girando` de 0 a 3 estreita e alarga. */
export function desenhaMoeda(c: Ctx, x: number, y: number, girando: number): void {
  const w = [4, 3, 2, 3][girando % 4]!;
  const ox = (4 - w) / 2;
  rect(c, x + ox - 1, y - 1, w + 2, 6, '#8a5a10'); // contorno
  rect(c, x + ox, y, w, 4, '#ffd23f');
  rect(c, x + ox, y, w, 1, '#fff3a8');
  if (w > 2) rect(c, x + ox + 1, y + 1, 1, 2, '#c8901a');
}

/** Balde de pipoca (5×6): listras vermelhas e brancas com pipoca por cima. */
export function desenhaPipoca(c: Ctx, x: number, y: number, estoura: boolean): void {
  rect(c, x - 1, y - 1, 7, 8, '#3a2a20'); // contorno
  rect(c, x, y + 2, 5, 4, '#ffffff');
  rect(c, x, y + 2, 1, 4, '#d63b3b');
  rect(c, x + 2, y + 2, 1, 4, '#d63b3b');
  rect(c, x + 4, y + 2, 1, 4, '#d63b3b');
  rect(c, x, y, 5, 2, '#fff1b0'); // pipoca
  rect(c, x + 1, y - 1, 1, 1, '#fff1b0');
  rect(c, x + 3, y - 1, 1, 1, '#fff1b0');
  if (estoura) rect(c, x + 2, y - 4, 1, 1, '#fff1b0'); // uma pulou
}

/** Brilho do espelho (cruz de 3 px) que pisca. */
export function desenhaBrilho(c: Ctx, x: number, y: number, t: number, reduzido: boolean): void {
  const q = quadro(t, 350, 3, reduzido);
  if (q === 2) return;
  const cor = q === 0 ? '#ffffff' : '#bfe8ff';
  rect(c, x, y - 1, 1, 3, cor);
  rect(c, x - 1, y, 3, 1, cor);
}
